import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useRouter,
  type RouterHistory,
} from '@tanstack/react-router';
import { useCallback } from 'react';
import { MeetingCompanionHost } from './components/meeting-companion/meeting-companion-host.tsx';
import { Shell, ShellSkeleton } from './components/shell/shell.tsx';
import { tokenStore } from './lib/auth.ts';
import { SessionProvider } from './lib/session.tsx';
import { AuthCallbackPage } from './routes/auth-callback.tsx';
import { AuditPage } from './routes/audit.tsx';
import { CandidatePage } from './routes/candidate.tsx';
import { CandidatesPage } from './routes/candidates.tsx';
import { CompaniesPage } from './routes/companies.tsx';
import { DuplicatesPage } from './routes/duplicates.tsx';
import { FieldsPage } from './routes/fields.tsx';
import { NoteTemplatesPage } from './routes/note-templates.tsx';
import { FlagsPage } from './routes/flags.tsx';
import { HomePage } from './routes/home.tsx';
import { MeetingCompanionPage } from './routes/meeting-companion.tsx';
import { MyTasksPage } from './routes/my-tasks.tsx';
import { TeamTasksPage } from './routes/team-tasks.tsx';
import { NotFoundPage } from './routes/not-found.tsx';
import { PipelinesPage } from './routes/pipelines.tsx';
import { ProjectSettingsPage } from './routes/project-settings.tsx';
import { DealsPage } from './routes/deals.tsx';
import { JobsPage } from './routes/jobs.tsx';
import { ProjectPage, ProjectsPage } from './routes/projects.tsx';
import { RecordCountsPage } from './routes/record-counts.tsx';
import { RolesPage } from './routes/roles.tsx';
import { SettingsPage } from './routes/settings.tsx';
import { WebhooksPage } from './routes/webhooks.tsx';
import { SystemHealthPage } from './routes/system-health.tsx';
import { ApiKeysPage } from './routes/api-keys.tsx';
import { SignInPage } from './routes/sign-in.tsx';
import { TeamsPage } from './routes/teams.tsx';
import { UsersPage } from './routes/users.tsx';

/**
 * The route tree, as code (RELAY-937). Two public routes; everything else sits under the
 * `authed` layout, whose `beforeLoad` sends a signed-out browser to /sign-in (with where it
 * was going) and whose component loads the session and draws the shell around the page.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (s: Record<string, unknown>): { returnTo?: string; error?: string } => ({
    ...(typeof s.returnTo === 'string' && s.returnTo.startsWith('/')
      ? { returnTo: s.returnTo }
      : {}),
    ...(typeof s.error === 'string' ? { error: s.error } : {}),
  }),
  component: SignInPage,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackPage,
});

function AuthedLayout() {
  const router = useRouter();
  const onUnauthenticated = useCallback(() => {
    void router.navigate({
      to: '/sign-in',
      search: { returnTo: window.location.pathname },
      replace: true,
    });
  }, [router]);
  return (
    <SessionProvider onUnauthenticated={onUnauthenticated} fallback={<ShellSkeleton />}>
      <Shell>
        <Outlet />
      </Shell>
      {/* Above the routes, so its window survives navigation (TS-1963). */}
      <MeetingCompanionHost />
    </SessionProvider>
  );
}

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: ({ location }) => {
    if (!tokenStore.get()) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router's redirect() is thrown by design
      throw redirect({ to: '/sign-in', search: { returnTo: location.pathname } });
    }
  },
  component: AuthedLayout,
});

// The whole table state travels as ONE opaque `view` parameter (RELAY-942); the page decodes it.
const candidatesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/candidates',
  // `open` is the candidate shown in the side panel (RELAY-944): an overlay, so the table stays mounted.
  validateSearch: (s: Record<string, unknown>): { view?: string; open?: string } => ({
    ...(typeof s.view === 'string' && s.view ? { view: s.view } : {}),
    ...(typeof s.open === 'string' && s.open ? { open: s.open } : {}),
  }),
  component: CandidatesPage,
});

const candidateRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/candidates/$id',
  component: CandidatePage,
});

const projectsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/projects',
  // `view` is the list's whole state — filter, sort, search, layout, card fields (RELAY-1038).
  validateSearch: (s: Record<string, unknown>): { view?: string } =>
    typeof s.view === 'string' && s.view ? { view: s.view } : {},
  component: ProjectsPage,
});
// `job` opens the Jobs tab with that job expanded and scrolled into view (RELAY-953).
const projectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/projects/$id',
  validateSearch: (s: Record<string, unknown>): { job?: string; tab?: 'deals' | 'messages' } => ({
    ...(typeof s.job === 'string' && s.job ? { job: s.job } : {}),
    // `tab=deals`: a deal opened from the Deals page lands on its project's Deals tab (RELAY-1038).
    // `tab=messages`: a pending message opened from Home lands on the Messages tab.
    ...(s.tab === 'deals' || s.tab === 'messages' ? { tab: s.tab } : {}),
  }),
  component: ProjectPage,
});
const companiesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/companies',
  validateSearch: (s: Record<string, unknown>): { view?: string; open?: string } => ({
    ...(typeof s.view === 'string' && s.view ? { view: s.view } : {}),
    ...(typeof s.open === 'string' && s.open ? { open: s.open } : {}),
  }),
  component: CompaniesPage,
});
const dealsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/deals',
  validateSearch: (s: Record<string, unknown>): { view?: string } =>
    typeof s.view === 'string' && s.view ? { view: s.view } : {},
  component: DealsPage,
});
const jobsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/jobs',
  validateSearch: (s: Record<string, unknown>): { view?: string } =>
    typeof s.view === 'string' && s.view ? { view: s.view } : {},
  component: JobsPage,
});

const page = <P extends string>(path: P, component: () => React.JSX.Element) =>
  createRoute({ getParentRoute: () => authedRoute, path, component });

const routeTree = rootRoute.addChildren([
  signInRoute,
  callbackRoute,
  authedRoute.addChildren([
    page('/', HomePage),
    candidatesRoute,
    candidateRoute,
    projectsRoute,
    projectRoute,
    companiesRoute,
    dealsRoute,
    jobsRoute,
    page('/users', UsersPage),
    page('/teams', TeamsPage),
    page('/audit', AuditPage),
    page('/flags', FlagsPage),
    page('/settings', SettingsPage),
    page('/settings/fields', FieldsPage),
    page('/settings/note-templates', NoteTemplatesPage),
    page('/settings/roles', RolesPage),
    page('/settings/pipelines', PipelinesPage),
    page('/settings/projects', ProjectSettingsPage),
    page('/settings/api-keys', ApiKeysPage),
    page('/settings/webhooks', WebhooksPage),
    page('/settings/system-health', SystemHealthPage),
    page('/tasks', MyTasksPage),
    page('/team-tasks', TeamTasksPage),
    page('/record-counts', RecordCountsPage),
    page('/candidates/duplicates', DuplicatesPage),
    page('/meeting-companion', MeetingCompanionPage),
  ]),
]);

export function createAppRouter(opts: { queryClient: QueryClient; history?: RouterHistory }) {
  return createRouter({
    routeTree,
    context: { queryClient: opts.queryClient },
    ...(opts.history ? { history: opts.history } : {}),
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
  interface HistoryState {
    /** A text conversation to open on the Messages tab: its key holds a phone, so never the URL. */
    conversation?: string;
  }
}
